import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
    IgxButtonModule,
    IgxDropDownModule,
    IgxGridModule,
    IgxHierarchicalGridModule,
    IgxIconButtonDirective,
    IgxIconModule,
    IgxRippleModule,
    IgxToggleActionDirective,
    IgxTooltipModule
} from '@infragistics/igniteui-angular';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { QRCodeComponent } from 'angularx-qrcode';
import { IgxPieChartModule } from 'igniteui-angular-charts';
import { MarkdownModule } from 'ngx-markdown';

import { MaterialModule } from '../core/material.module';
import { DirectivesModule } from '../directives/directives.module';
import { PipesModule } from '../pipes/pipes.module';
import { AlertComponent } from './alert/alert.component';
import { ChatConversationComponent } from './chat-conversation/chat-conversation.component';
import { AddressDialogComponent } from './dialogs/address/address-dialog.component';
import { AgendaItemImportVotesComponent } from './dialogs/agenda-item-import-votes/agenda-item-import-votes.component';
import { AgendaItemSettingsComponent } from './dialogs/agenda-item-settings/agenda-item-settings.component';
import { AgendaItemsComponent } from './dialogs/agenda-items/agenda-items.component';
import { BarcodeScannerDialog } from './dialogs/barcode-scanner/barcode-scanner.dialog';
import { ChangeEmailPhoneComponent } from './dialogs/change-email-phone/change-email-phone.component';
import { ChangePasswordComponent } from './dialogs/change-password/change-password.component';
import { GateDeviceCodeDialogComponent } from './dialogs/gate-device-code-dialog/gate-device-code-dialog.component';
import { GateQrCodeComponent } from './dialogs/gate-qr-code/gate-qr-code.component';
import { GeneralMeetingExportDialogComponent } from './dialogs/general-meeting-export-dialog/general-meeting-export-dialog.component';
import { GeneralMeetingImportDialogComponent } from './dialogs/general-meeting-import-dialog/general-meeting-import-dialog.component';
import { GeneralMeetingSettingsDialog } from './dialogs/general-meeting-settings/general-meeting-settings.dialog';
import { GeneralPromptDialog } from './dialogs/general-prompt-dialog/general-prompt.dialog';
import { NoServerConnectionDialog } from './dialogs/no-server-connection/no-server-connection.dialog';
import { ReportScheduleDialogParameterFieldComponent } from './dialogs/report-schedule-dialog/parameter-field/report-schedule-dialog-parameter-field.component';
import { ReportScheduleDialogComponent } from './dialogs/report-schedule-dialog/report-schedule-dialog.component';
import { ServerResponseComponent } from './dialogs/server-response/server-response.component';
import { TermsOfServiceDialog } from './dialogs/terms-of-service/terms-of-service.dialog';
import { UsersDialogFormComponent } from './dialogs/users/users-dialog-form.component';
import { VotingDialogComponent } from './dialogs/voting/voting-dialog.component';
import { DmiStatusIndicatorComponent } from './dmi/dmi-status-indicator/dmi-status-indicator.component';
import { DropdownComponent } from './dropdown/dropdown.component';
import { FavoritesComponent } from './favorites/favorites.component';
import { AutoCompleteComponent } from './forms/auto-complete/auto-complete.component';
import { CronInputComponent } from './forms/cron-input/cron-input.component';
import { DatepickerComponent } from './forms/datepicker/datepicker.component';
import { DatetimeInputComponent } from './forms/datetime-input/datetime-input.component';
import { DynamicFormFieldComponent } from './forms/dynamic-form-field/dynamic-form-field.component';
import { EntityAutocompleteComponent } from './forms/entity-autocomplete/entity-autocomplete.component';
import { FormFieldErrorControlComponent } from './forms/form-field-error-control/form-field-error-control.component';
import { InputComponent } from './forms/input/input.component';
import { ModalFormComponent } from './forms/modal-form/modal-form.component';
import { RelativeDateInputComponent } from './forms/relative-date-input/relative-date-input.component';
import { TextareaComponent } from './forms/textarea/textarea.component';
import { LayoutsModule } from './layouts/layouts.module';
import { GridColumnDirective } from './paged-grid/grid-column.directive';
import { PagedGridComponent } from './paged-grid/paged-grid.component';
import { AddressComponent } from './partials/address/address.component';
import { MutationsComponent } from './partials/mutations/mutations.component';
import { PaymentAccountsComponent } from './partials/payment-accounts/payment-accounts.component';
import { ShareholderAccountsComponent } from './partials/shareholder-accounts/shareholder-accounts.component';
import { SubReportsComponent } from './partials/sub-reports/sub-reports.component';
import { TransactionsTableComponent } from './partials/transactions-table/transactions-table.component';
import { UsersPartialFormComponent } from './partials/users-form/users-partial-form.component';
import { VotingResultsComponent } from './partials/voting-results/voting-results.component';
import { ShareInfoComponent } from './share-info/share-info.component';
import { TopShareholdersComponent } from './top-shareholders/top-shareholders.component';
import { TopTransactionsComponent } from './top-transactions/top-transactions.component';
import { ChatControlComponent } from './widgets/chat-control/chat-control.component';
import { ChatComponent } from './widgets/chat/chat.component';
import { CountDisplayComponent } from './widgets/count-display/count-display.component';
import { DeliveryMethodComponent } from './widgets/delivery-method/delivery-method.component';
import { DeliveryMethodsStatisticComponent } from './widgets/delivery-methods-statistic/delivery-methods-statistic.component';
import { DmiIntegrationComponent } from './widgets/dmi-integration/dmi-integration.component';
import { FactsAndFiguresComponent } from './widgets/facts-and-figures/facts-and-figures.component';
import { FavoritesListComponent } from './widgets/favorites-list/favorites-list.component';
import { FileSharingComponent } from './widgets/file-sharing/file-sharing.component';
import { FileSharingFormComponent } from './widgets/file-sharing/form/file-sharing-form.component';
import { GeneralMeetingAgendaComponent } from './widgets/general-meeting-agenda/general-meeting-agenda.component';
import { GeneralMeetingInfoComponent } from './widgets/general-meeting-info/general-meeting-info.component';
import { GeneralMeetingRegistrationComponent } from './widgets/general-meeting-registration/general-meeting-registration.component';
import { SelfAttendanceDialog } from './widgets/general-meeting-registration/self-attendance/self-attendance.dialog';
import { GeneralMeetingSettingsComponent } from './widgets/general-meeting-settings/general-meeting-settings.component';
import { InfomaterialComponent } from './widgets/infomaterial/infomaterial.component';
import { LiveStreamVideoComponent } from './widgets/live-stream-widgets/live-stream-video/live-stream-video.component';
import { LiveStreamRequestToSpeakDialog } from './widgets/live-stream-widgets/live-stream/live-stream-request-to-speak/live-stream-request-to-speak.dialog';
import { LiveStreamComponent } from './widgets/live-stream-widgets/live-stream/live-stream.component';
import { LiveVotingComponent } from './widgets/live-voting/live-voting.component';
import { MarkdownComponent } from './widgets/markdown/markdown.component';
import { NoPermissionComponent } from './widgets/no-permission/no-permission.component';
import { PendingTransactionsComponent } from './widgets/pending-transactions/pending-transactions.component';
import { PersonalInfoComponent } from './widgets/personal-info/personal-info.component';
import { PresenceEditDialog } from './widgets/presence/presence-edit/presence-edit.dialog';
import { PresenceComponent } from './widgets/presence/presence.component';
import { ShareholderCategoriesComponent } from './widgets/shareholder-categories/shareholder-categories.component';
import { ShareholdersDomicileComponent } from './widgets/shareholders-domicile/shareholders-domicile.component';
import { TopShareholdersChartComponent } from './widgets/top-shareholders-chart/top-shareholders-chart.component';
import { TotalDispoPercentComponent } from './widgets/total-dispo-percent/total-dispo-percent.component';
import { TotalDispoComponent } from './widgets/total-dispo/total-dispo.component';
import { TotalRegisteredPercentComponent } from './widgets/total-registered-percent/total-registered-percent.component';
import { TotalRegisteredComponent } from './widgets/total-registered/total-registered.component';
import { TotalShareholdersComponent } from './widgets/total-shareholders/total-shareholders.component';
import { VirtualSpeakersDeskComponent } from './widgets/virtual-speakers-desk/virtual-speakers-desk.component';
import { VotingMonitorComponent } from './widgets/voting-monitor/voting-monitor.component';

@NgModule({
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        MaterialModule,
        LayoutsModule,
        IgxGridModule,
        IgxTooltipModule,
        IgxHierarchicalGridModule,
        IgxIconModule,
        IgxDropDownModule,
        IgxButtonModule,
        IgxIconButtonDirective,
        IgxRippleModule,
        IgxPieChartModule,
        TranslatePipe,
        TranslateDirective,
        DirectivesModule,
        PipesModule,
        MarkdownModule.forChild(),
        IgxToggleActionDirective,
        QRCodeComponent
    ],
    declarations: [
        PagedGridComponent,
        TopShareholdersComponent,
        TopTransactionsComponent,
        TotalShareholdersComponent,
        TotalDispoComponent,
        TotalRegisteredComponent,
        CountDisplayComponent,
        PendingTransactionsComponent,
        AutoCompleteComponent,
        AddressComponent,
        FormFieldErrorControlComponent,
        InputComponent,
        TextareaComponent,
        DatepickerComponent,
        DatetimeInputComponent,
        RelativeDateInputComponent,
        CronInputComponent,
        FavoritesComponent,
        FavoritesListComponent,
        DropdownComponent,
        FileSharingComponent,
        FileSharingFormComponent,
        MutationsComponent,
        ShareInfoComponent,
        TransactionsTableComponent,
        ShareholderAccountsComponent,
        AddressDialogComponent,
        SubReportsComponent,
        ServerResponseComponent,
        PaymentAccountsComponent,
        GeneralMeetingRegistrationComponent,
        GeneralMeetingAgendaComponent,
        ChangeEmailPhoneComponent,
        AgendaItemsComponent,
        PersonalInfoComponent,
        AlertComponent,
        GridColumnDirective,
        TopShareholdersChartComponent,
        TotalDispoPercentComponent,
        TotalRegisteredPercentComponent,
        InfomaterialComponent,
        ShareholderCategoriesComponent,
        FactsAndFiguresComponent,
        ModalFormComponent,
        UsersPartialFormComponent,
        UsersDialogFormComponent,
        ChatControlComponent,
        ChatComponent,
        ChatConversationComponent,
        ShareholdersDomicileComponent,
        DeliveryMethodsStatisticComponent,
        LiveVotingComponent,
        VotingDialogComponent,
        DeliveryMethodComponent,
        GeneralMeetingSettingsComponent,
        GeneralMeetingInfoComponent,
        MarkdownComponent,
        PresenceComponent,
        VotingResultsComponent,
        LiveStreamComponent,
        LiveStreamVideoComponent,
        ChangePasswordComponent,
        VotingMonitorComponent,
        NoPermissionComponent,
        VirtualSpeakersDeskComponent,
        AgendaItemSettingsComponent,
        GeneralMeetingExportDialogComponent,
        GeneralMeetingImportDialogComponent,
        AgendaItemImportVotesComponent,
        ReportScheduleDialogComponent,
        ReportScheduleDialogParameterFieldComponent,
        GateQrCodeComponent,
        GateDeviceCodeDialogComponent,
        DmiStatusIndicatorComponent,
        DmiIntegrationComponent,
        SelfAttendanceDialog,
        GeneralMeetingSettingsDialog,
        BarcodeScannerDialog,
        TermsOfServiceDialog,
        EntityAutocompleteComponent,
        DynamicFormFieldComponent,
        NoServerConnectionDialog,
        GeneralPromptDialog,
        PresenceEditDialog,
        LiveStreamRequestToSpeakDialog
    ],
    exports: [
        LayoutsModule,
        CountDisplayComponent,
        PagedGridComponent,
        TopShareholdersComponent,
        TopTransactionsComponent,
        TotalShareholdersComponent,
        TotalDispoComponent,
        TotalRegisteredComponent,
        PendingTransactionsComponent,
        AutoCompleteComponent,
        AddressComponent,
        FormFieldErrorControlComponent,
        InputComponent,
        TextareaComponent,
        DatepickerComponent,
        DatetimeInputComponent,
        RelativeDateInputComponent,
        CronInputComponent,
        FavoritesComponent,
        FavoritesListComponent,
        DropdownComponent,
        FileSharingComponent,
        FileSharingFormComponent,
        MutationsComponent,
        ShareInfoComponent,
        TransactionsTableComponent,
        ShareholderAccountsComponent,
        AddressDialogComponent,
        SubReportsComponent,
        ServerResponseComponent,
        PaymentAccountsComponent,
        GeneralMeetingRegistrationComponent,
        GeneralMeetingAgendaComponent,
        ChangeEmailPhoneComponent,
        AgendaItemsComponent,
        PersonalInfoComponent,
        AlertComponent,
        GridColumnDirective,
        TopShareholdersChartComponent,
        TotalDispoPercentComponent,
        TotalRegisteredPercentComponent,
        InfomaterialComponent,
        ShareholderCategoriesComponent,
        FactsAndFiguresComponent,
        ModalFormComponent,
        UsersPartialFormComponent,
        UsersDialogFormComponent,
        ChatControlComponent,
        ChatComponent,
        DeliveryMethodsStatisticComponent,
        LiveVotingComponent,
        VotingDialogComponent,
        DeliveryMethodComponent,
        GeneralMeetingSettingsComponent,
        GeneralMeetingInfoComponent,
        MarkdownComponent,
        PresenceComponent,
        VotingResultsComponent,
        LiveStreamComponent,
        LiveStreamVideoComponent,
        ChangePasswordComponent,
        VotingMonitorComponent,
        VirtualSpeakersDeskComponent,
        AgendaItemSettingsComponent,
        GeneralMeetingExportDialogComponent,
        GeneralMeetingImportDialogComponent,
        AgendaItemImportVotesComponent,
        ReportScheduleDialogComponent,
        ReportScheduleDialogParameterFieldComponent,
        GateQrCodeComponent,
        GateDeviceCodeDialogComponent,
        DmiStatusIndicatorComponent,
        DmiIntegrationComponent,
        BarcodeScannerDialog,
        EntityAutocompleteComponent,
        DynamicFormFieldComponent,
        GeneralPromptDialog
    ]
})
export class ComponentsModule {}
